import { useState, useEffect } from 'react';
import { Truck, Package, Layers, Calculator, Compass, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export const ONBOARDING_KEY = 'freight-cbm-onboarding-done';
export const REPLAY_ONBOARDING_EVENT = 'cbm:replay-onboarding';

interface TourStep {
  title: string;
  body: string;
  icon: LucideIcon;
}

const steps: TourStep[] = [
  {
    title: 'Welcome to Freight CBM!',
    body: 'Quickly calculate cubic meters, dimensional weight, and shipping costs for air, ocean, and courier freight — no spreadsheet required.',
    icon: Truck,
  },
  {
    title: 'Enter Package Details',
    body: 'Add dimensions (L×W×H), weight, and quantity for each package line. Switch units independently per column — or paste rows straight from a spreadsheet.',
    icon: Package,
  },
  {
    title: 'Choose Freight Mode',
    body: 'Select Air, Ocean LCL, or Courier to apply the correct DIM divisor automatically. Override it anytime with the DIM Divisor field.',
    icon: Layers,
  },
  {
    title: 'View Results Instantly',
    body: 'Total CBM, chargeable weight, and density update in real-time as you type. Copy any value with a single click.',
    icon: Calculator,
  },
  {
    title: 'Explore More',
    body: 'Use the Optimization tab for container fit & cost analysis, Reference for formulas & glossary, and History to save & recall calculations.',
    icon: Compass,
  },
];

export function replayOnboardingTour() {
  localStorage.removeItem(ONBOARDING_KEY);
  window.dispatchEvent(new CustomEvent(REPLAY_ONBOARDING_EVENT));
}

export function OnboardingTour() {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const done = localStorage.getItem(ONBOARDING_KEY);
    if (!done) setVisible(true);

    const handleReplay = () => {
      setStep(0);
      setVisible(true);
    };
    window.addEventListener(REPLAY_ONBOARDING_EVENT, handleReplay);
    return () => window.removeEventListener(REPLAY_ONBOARDING_EVENT, handleReplay);
  }, []);

  const handleClose = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  const current = steps[step]!;
  const isLast = step === steps.length - 1;
  const StepIcon = current.icon;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Onboarding tour"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        padding: 'var(--space-4)',
      }}
    >
      <div className="onboarding-tour-card">
        <button
          type="button"
          className="btn btn-sm"
          onClick={handleClose}
          aria-label="Close tour"
          title="Close tour"
          style={{ position: 'absolute', top: 'var(--space-3)', right: 'var(--space-3)', padding: 'var(--space-1)' }}
        >
          <X size={16} />
        </button>

        <div className="onboarding-tour-progress" aria-hidden="true">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`onboarding-tour-progress-dot${i === step ? ' is-active' : i < step ? ' is-complete' : ''}`}
            />
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-accent-light)',
              color: 'var(--color-accent)',
            }}
          >
            <StepIcon size={24} strokeWidth={1.75} />
          </span>

          <div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-1)' }}>
              Step {step + 1} of {steps.length}
            </div>
            <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', marginBottom: 'var(--space-2)' }}>
              {current.title}
            </h2>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 0 }}>
              {current.body}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-5)' }}>
          <button type="button" className="btn btn-sm" onClick={handleClose}>
            Skip tour
          </button>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {step > 0 && (
              <button type="button" className="btn btn-sm" onClick={() => setStep(step - 1)} aria-label="Previous step">
                Back
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (isLast) {
                  handleClose();
                } else {
                  setStep(step + 1);
                }
              }}
            >
              {isLast ? "Let's go!" : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
